import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiService } from '../services/apiService';
import { DSCard, DSInput } from '../design-system/primitives';
import './KnowledgeBase.css';

function KnowledgeBaseCategory() {
  const { slug } = useParams();
  const [category, setCategory] = useState(null);
  const [articles, setArticles] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isActive = true;

    const fetchCategory = async () => {
      try {
        setLoading(true);
        const response = await apiService.getKnowledgeBaseCategory(slug);
        if (!isActive) {
          return;
        }
        setCategory(response.data.category);
        setArticles(response.data.articles || []);
        setError(null);
      } catch (err) {
        if (!isActive) {
          return;
        }
        setError('Unable to load this category.');
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    fetchCategory();
    return () => {
      isActive = false;
    };
  }, [slug]);

  const filteredArticles = useMemo(() => {
    if (!query.trim()) {
      return articles;
    }
    const lowered = query.toLowerCase();
    return articles.filter((article) => (
      article.title?.toLowerCase().includes(lowered) ||
      article.excerpt?.toLowerCase().includes(lowered)
    ));
  }, [articles, query]);

  if (loading) {
    return (
      <div className="kb-page">
        <div className="kb-content">
          <DSCard className="kb-hero" cornerVariant="lg">
            <h1>Loading category...</h1>
          </DSCard>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="kb-page">
        <div className="kb-content">
          <DSCard className="kb-hero" cornerVariant="lg">
            <h1>Help Center</h1>
            <p>{error}</p>
          </DSCard>
        </div>
      </div>
    );
  }

  return (
    <div className="kb-page">
      <div className="kb-content">
        <div className="kb-breadcrumbs">
          <Link to="/help">Help Center</Link>
          <span>/</span>
          <span>{category?.name}</span>
        </div>

        <DSCard className="kb-hero" cornerVariant="lg">
          <h1>{category?.name}</h1>
          <p>{category?.description || 'Browse articles in this category.'}</p>
          <div className="kb-search">
            <DSInput
              type="search"
              placeholder="Search in this category"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              aria-label="Search this category"
            />
          </div>
        </DSCard>

        <section className="kb-section">
          <h2>Articles</h2>
          <div className="kb-article-list">
            {filteredArticles.map((article) => (
              <Link
                key={article.id}
                to={`/help/article/${article.slug}`}
                className="kb-article-item"
              >
                <h3>{article.title}</h3>
                <div
                  className="kb-excerpt"
                  dangerouslySetInnerHTML={{ __html: article.excerpt || '' }}
                />
              </Link>
            ))}
            {filteredArticles.length === 0 && (
              <DSCard className="kb-empty" cornerVariant="md">No articles found in this category.</DSCard>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

export default KnowledgeBaseCategory;
